import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../ui/accordion";
import { Reveal, Eyebrow, GoldCta, IMAGES, WA_LINK, openWhatsApp, scrollToId } from "./shared";

const manifestoLines = [
  "Ha bisogno di uomini che amino Dio.",
  "Che sappiano chi sono.",
  "Che governino il proprio corpo.",
  "Che governino i propri impulsi.",
  "Che proteggano e servano la propria famiglia.",
  "Che costruiscano.",
  "Che provvedano.",
  "Che abbiano il coraggio di esporsi.",
  "Che trasformino ciò che hanno ricevuto in missione, impatto e reddito.",
];

export const ManifestoSection = () => (
  <section className="relative bg-black px-5 sm:px-10 py-28 sm:py-40 overflow-hidden" data-testid="manifesto-section">
    <div className="absolute inset-0 opacity-[0.08]">
      <img src={IMAGES.lion} alt="" className="w-full h-full object-cover" />
    </div>
    <div className="relative max-w-4xl mx-auto text-center">
      <Reveal>
        <Eyebrow className="mb-6">Manifesto</Eyebrow>
        <h2 className="font-display uppercase text-3xl sm:text-4xl lg:text-5xl leading-tight text-[#fbf9f5]" data-testid="manifesto-title">
          Il mondo non ha bisogno di altri <span className="gold-text">uomini tiepidi.</span>
        </h2>
      </Reveal>
      <div className="mt-16 space-y-8">
        {manifestoLines.map((line, i) => (
          <motion.p
            key={line}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.9, delay: 0.08, ease: [0.16, 1, 0.3, 1] }}
            className="font-quote italic text-xl sm:text-2xl lg:text-3xl text-[#b8b2a7]"
          >
            {line}
          </motion.p>
        ))}
      </div>
      <Reveal className="mt-20">
        <p className="font-display text-3xl sm:text-5xl lg:text-6xl uppercase gold-text tracking-[0.1em]" data-testid="manifesto-pillars">
          Fede. Fitness. Finanze.
        </p>
      </Reveal>
    </div>
  </section>
);

export const FinalCtaSection = () => (
  <section className="relative min-h-[90vh] flex items-center overflow-hidden" data-testid="final-cta-section">
    <img src={IMAGES.finalCta} alt="Uomo davanti all'alba in montagna" className="absolute inset-0 w-full h-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#080706]/95 via-[#080706]/70 to-[#080706]/40" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#080706] via-transparent to-[#080706]/60" />
    <div className="relative max-w-6xl mx-auto px-5 sm:px-10 lg:px-20 py-28 w-full">
      <Reveal>
        <h2 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-[1.08] max-w-3xl" data-testid="final-cta-title">
          Diventa l'uomo che sai di <span className="gold-text">dover essere.</span>
        </h2>
        <p className="font-quote italic text-xl sm:text-2xl text-[#f3e5ab] mt-6">Spiritualmente. Fisicamente. Economicamente.</p>
        <div className="text-[#b8b2a7] mt-8 space-y-1.5 text-base md:text-lg">
          <p>Non importa da dove parti.</p>
          <p>Non importa se oggi hai già un'attività.</p>
          <p>Non importa se hai 20, 30, 40 o 50 anni.</p>
          <p>Non importa se pensi di non avere ancora competenze.</p>
          <p className="text-[#fbf9f5] font-semibold pt-2">Partiamo da dove sei.</p>
        </div>
        <div className="mt-10">
          <GoldCta testId="final-cta-button">Candidati per lavorare con me</GoldCta>
        </div>
        <div className="mt-14">
          <p className="font-display text-lg tracking-[0.3em] uppercase text-[#fbf9f5]">Tempio Vivo</p>
          <p className="text-sm text-[#d4af37] mt-1.5">Fede • Fitness • Finanze</p>
          <p className="font-quote italic text-sm text-[#b8b2a7] mt-1.5">Il metodo di coaching cristiano di Fede & Fitness Italia.</p>
        </div>
      </Reveal>
    </div>
  </section>
);

const faqs = [
  { q: "Devo essere già un cristiano praticante?", a: "Il percorso si fonda sui principi della fede cristiana e sull'insegnamento di Gesù. È pensato per uomini disponibili a confrontarsi con questi valori e a metterli in pratica." },
  { q: "È un percorso spirituale o pratico?", a: "Entrambi. La fede è il fondamento, ma il lavoro riguarda anche disciplina, abitudini, relazioni, comunicazione, talenti e responsabilità quotidiane." },
  { q: "È una psicoterapia?", a: "No. Uomo Biblico è un percorso di life coaching e mentorship cristiana. Non sostituisce psicoterapia, assistenza psicologica, diagnosi o trattamenti sanitari." },
  { q: "Devo avere già un business?", a: "No. Il percorso può partire dall'identità, dalla disciplina e dai talenti personali. Non è necessario avere già un'attività." },
  { q: "Quanto costa il percorso?", a: "Puoi scrivere direttamente a Elio su WhatsApp per conoscere le modalità, la durata e il compenso della mentorship." },
  { q: "Come posso iniziare?", a: "Clicca sul pulsante WhatsApp e scrivi direttamente a Elio. Raccontagli brevemente la tua situazione e cosa desideri cambiare. Potrete confrontarvi per comprendere se il percorso è adatto a te." },
];

export const FaqSection = () => (
  <section id="faq" className="relative px-5 sm:px-10 lg:px-20 py-20 sm:py-28 bg-[#0a0908]">
    <div className="max-w-3xl mx-auto">
      <Reveal className="text-center">
        <Eyebrow className="mb-4">Domande Frequenti</Eyebrow>
        <h2 className="font-display uppercase text-3xl sm:text-4xl" data-testid="faq-title">FAQ</h2>
      </Reveal>
      <Reveal delay={0.1} className="mt-12">
        <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
          {faqs.map((f, i) => (
            <AccordionItem key={f.q} value={`faq-${i}`} className="ub-card rounded-xl px-6 border-b-0">
              <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left text-sm sm:text-base font-semibold text-[#fbf9f5] hover:text-[#f3e5ab] hover:no-underline py-5">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm text-[#b8b2a7] leading-relaxed pb-5">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </div>
  </section>
);

export const Footer = () => (
  <footer className="border-t border-[#d4af37]/15 bg-[#080706] px-5 sm:px-10 py-12" data-testid="footer">
    <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-8">
      <div className="text-center sm:text-left">
        <p className="font-display text-base tracking-[0.25em] gold-text font-bold">UOMO BIBLICO</p>
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#b8b2a7] mt-1.5">By Elio Briotti</p>
        <p className="text-xs text-[#d4af37] mt-3">Cuore • Voce • Eredità</p>
        <p className="text-xs text-[#7a7367] mt-2">Un progetto collegato a Fede & Fitness Italia.</p>
      </div>
      <nav className="flex flex-wrap items-center justify-center gap-x-7 gap-y-3">
        {[["storia", "La mia storia"], ["metodo", "Il metodo"], ["mentorship", "Mentorship"]].map(([id, label]) => (
          <button
            key={id}
            data-testid={`footer-link-${id}`}
            onClick={() => scrollToId(id)}
            className="text-[11px] uppercase tracking-[0.2em] text-[#b8b2a7] hover:text-[#f3e5ab] transition-colors duration-300"
          >
            {label}
          </button>
        ))}
        <a
          href={WA_LINK}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="footer-whatsapp"
          className="cta-ghost rounded-full px-6 py-3 text-[11px] uppercase inline-flex items-center gap-2"
        >
          <MessageCircle size={14} /> WhatsApp
        </a>
      </nav>
    </div>
    <p className="text-center text-[10px] text-[#4d4841] mt-10">© {new Date().getFullYear()} Uomo Biblico — Elio Briotti. Tutti i diritti riservati.</p>
  </footer>
);

export const StickyMobileCta = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div
      data-testid="sticky-mobile-cta"
      className={`fixed bottom-0 inset-x-0 z-50 lg:hidden transition-transform duration-500 ${visible ? "translate-y-0" : "translate-y-full"}`}
    >
      <div className="bg-[#080706]/95 backdrop-blur-xl border-t border-[#d4af37]/25 px-4 py-3 flex items-center justify-between gap-3">
        <p className="text-[10px] uppercase tracking-[0.15em] text-[#b8b2a7] leading-tight">
          Uomo Biblico<br /><span className="text-[#d4af37]">Mentorship Cristiana 1:1</span>
        </p>
        <button
          data-testid="sticky-cta-button"
          onClick={openWhatsApp}
          className="cta-gold rounded-full px-5 py-3 text-[11px] uppercase shrink-0"
        >
          Scrivimi su WhatsApp
        </button>
      </div>
    </div>
  );
};

export const WhatsAppFloat = () => (
  <a
    href={WA_LINK}
    target="_blank"
    rel="noopener noreferrer"
    data-testid="whatsapp-float"
    className="fixed bottom-20 lg:bottom-8 right-4 lg:right-8 z-40 w-12 h-12 rounded-full bg-[#d4af37] text-[#080706] flex items-center justify-center shadow-[0_10px_40px_-10px_rgba(212,175,55,0.6)] hover:scale-110 transition-transform duration-300"
    aria-label="Scrivi su WhatsApp"
  >
    <MessageCircle size={22} />
  </a>
);
